import React, { useEffect, useMemo, useState } from 'react'
import { Route } from 'react-router'
import {
  SmartList,
  IColumnElement,
  Icons,
  IHeaderItem,
  IconButton,
  SearchInput,
  PaginationPanel,
  IRowElement,
  Spinner,
  Container,
  Row,
} from '@doseme/cohesive-ui'
import { BG_WHITE, DARKBLUE, NAVY } from '@doseme/cohesive-ui'
import { faClone } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IEntityHashMap, isLoading } from 'flux-entities'

import { ShowContainer } from '../Show'
import { TProps } from './types'
import { IHospital } from '../../../../types/hospital'
import { hospitalsEdit, hospitalsShow, HOSPITALS_SHOW_URL, hospitalsSubscriptionsEdit } from '../../../../constants/urls/frontend'
import { DISPLAY_ITEMS_PER_PAGE } from '../../../../constants/display'
import { paginate } from '../../../utils'
import { smartListData } from '../../utils'
import { alphabetical } from '../../../../utils/arrayUtils'
import { IHospitalsState } from '../../../../store/hospitals/types'
import { IInstallationsState } from '../../../../store/installations/types'
import { IInstallation } from '../../../../types/installation'

const cols: IHeaderItem[] = [
  {
    name: 'Installation',
  },
  {
    name: 'Instance ID',
  },
  {
    name: 'Name',
  },
  {
    name: 'Timezone',
  },
  {
    name: 'Actions',
  }
]

const textContent = (hospital: IHospital, allInstallations: IEntityHashMap<IInstallation>): string[] => {
  const installation = allInstallations[hospital.installation] ? allInstallations[hospital.installation].code : ''
  return [
    installation,
    hospital.instanceId.toString(),
    hospital.name,
    `${installation}/${hospital.instanceId}`,
    hospital.timezone
  ]
}

// This method is memoized with useMemo() upon being called.
const getSearchableContent = (
  hospitalIds: Array<number | string>,
  allHospitals: IEntityHashMap<IHospital>,
  allInstallations: IEntityHashMap<IInstallation>
) => hospitalIds.reduce<Record<number, string[]>>(
  (acc, x) => {
    return {
      ...acc,
      [x]: textContent(allHospitals[x], allInstallations)
    }
  }, {}
)

// This returns row data for all possible inputs and states of the Smart List, including search and pagination
const getPaginatedListWithPageCount = (
  isPageLoading: boolean,
  hospitals: IHospitalsState,
  installations: IInstallationsState,
  searchContent: Record<number, string[]>,
  searchText: string,
  columnContent: (hospital: IHospital) => IColumnElement[],
  // @ts-ignore
  history: History<any>,
  currentPage: number,
  updateCurrentPage: (pages: number) => void,
): { totalPages: number, paginatedData: IRowElement[] } => {
  let data: IRowElement[] = []

  if (!isPageLoading) {
    data = smartListData({
      ids: hospitals.ids.sort(
        (x, y) => alphabetical(
          installations.all[hospitals.all[x].installation].code,
          installations.all[hospitals.all[y].installation].code,
        )
      ),
      all: hospitals.all,
      searchContent,
      searchText,
      columnizeRow: columnContent,
      getOnClickCallback: (entityId: number) => history.push(hospitalsShow(entityId))
    })
  }

  const totalPages = Math.ceil(data.length / DISPLAY_ITEMS_PER_PAGE) || 1

  if (currentPage > totalPages) {
    updateCurrentPage(totalPages)
  }

  return {
    totalPages,
    paginatedData: paginate(data, { currentPage, itemsPerPage: DISPLAY_ITEMS_PER_PAGE })
  }
}

const Display: React.FC<TProps> = (props: TProps): JSX.Element | null => {
  const {
    hospitals,
    fetchHospitals,
    installations,
    fetchInstallations,
    history,
    fetchPlans,
    plans
  } = props

  useEffect(() => {
    if (!installations.touched) { fetchInstallations() }
  }, [installations.touched, fetchInstallations])

  useEffect(() => {
    if (!plans.touched) { fetchPlans() }
  }, [plans.touched, fetchPlans])

  useEffect(() => {
    if (!hospitals.touched) { fetchHospitals() }
  }, [hospitals.touched, fetchHospitals])

  const [currentPage, updateCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState('')

  const searchableContent = useMemo(() => getSearchableContent(
    hospitals.ids,
    hospitals.all,
    installations.all
  ), [
    hospitals.ids,
    hospitals.all,
    installations.all
  ])

  if (!hospitals.touched || !installations.touched || !plans.touched) {
    return null
  }

  const columnContent = (hospital: IHospital): IColumnElement[] => [
    {
      name: 'installation',
      element: (
        <span>
          {installations.all[hospital.installation].code}
        </span>
      ),
      text: installations.all[hospital.installation].code
    },

    {
      name: 'id',
      element: (
        <span>
          {hospital.instanceId}
        </span>
      ),
      text: hospital.instanceId.toString(),
    },

    {
      name: 'name',
      element: (
        <span>
          {hospital.name}
        </span>
      ),
      text: hospital.name,
    },

    {
      name: 'timezone',
      element: (
        <span>
          {hospital.timezone}
        </span>
      ),
      text: hospital.timezone,
    },

    {
      name: 'actions',
      element: (
        <div className='d-flex align-items-center p-3'>
          <IconButton
            background={BG_WHITE}
            data-test='edit-subscription-button'
            borderColor={DARKBLUE}
            color={DARKBLUE}
            size='30px'
            onClick={(e) => {
              e.stopPropagation()
              history.push(hospitalsSubscriptionsEdit(hospital.id))
            }}
            className='ml-2'
          >
            <FontAwesomeIcon icon={faClone} />
          </IconButton>

          <Icons.EditIconButton
            data-test='edit-button'
            onClick={(e) => {
              e.stopPropagation()
              history.push(hospitalsEdit(hospital.id))
            }}
            className='ml-2'
          />
        </div>
      )
    }
  ]

  const isPageLoading = isLoading(hospitals) || isLoading(installations) || isLoading(plans)

  const { totalPages, paginatedData } = getPaginatedListWithPageCount(
    isPageLoading,
    hospitals,
    installations,
    searchableContent,
    searchText,
    columnContent,
    history,
    currentPage,
    updateCurrentPage,
  )

  return (
    <div>
      <Container>
        <div className='d-flex justify-content-between my-2'>
          <div className='d-flex align-items-center'>
            <h4 className='co-h4 pr-2' data-test='hospitals-list'>Hospitals List</h4>
            {isPageLoading && <Spinner color={NAVY} size='lg' />}
          </div>
          <SearchInput
            value={searchText}
            onChange={setSearchText}
          />
        </div>
        <Row>
          <SmartList
            loading={isPageLoading}
            cols={cols}
            data={paginatedData}
            textIfEmpty={hospitals.ids.length ? 'No matching hospitals found.' : 'There are currently no hospitals.'}
          />
        </Row>
        <Row className='mt-2'>
          <PaginationPanel
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={updateCurrentPage}
          />
        </Row>
      </Container>
      <Route path={HOSPITALS_SHOW_URL} component={ShowContainer} />
    </div>
  )
}

export {
  Display
}
